import React, { useEffect, useRef, useState } from 'react';
import './ProjectSheet.css';

// ProjectSheet — THE SHEET SHE PULLS UP FOR *THIS* PROJECT. 2026-09-03.
//
// Braeden:
//   "the visuals dont need to be the exact same after every project... aria
//    needs to make a custom generator in real time... more tailored to the
//    persons need on what they are trying to do. if they say edit a video,
//    she asked a really good question, so its like pulling up a digital
//    sheet, with number 1, whats the video going to be about."
//
// Every project used to get the same three-row card. This one is generated
// per turn by services/ariaProjectSheet.js from THAT goal's own questions, so
// a video edit opens a Shot list and a broken civic opens a Work order — same
// component, different sheet, decided by what the person said.
//
// It is a LIVE sheet, not a slide: rows fill in as she files answers, the row
// she is asking right now is lit, and the last line turns into her first move
// when the picture is full. So it holds still while they talk and only ever
// changes where the conversation changed it.

const KIND_MARK = {
  video: '▶', vehicle: '⛭', money: '$', trade: '⚒', event: '✦',
  writing: '✎', body: '◈', home: '⌂', build: '◲', venture: '◆', plain: '•',
  // The backend has emitted these eight since 9/3 and every one was showing
  // the plain bullet, so a pet plan and a case file were marked the same.
  audio: '♪', family: '⚭', learn: '✧', work: '▤',
  pet: '❥', legal: '§', admin: '▦', gear: '⌁',
};

export default function ProjectSheet({ sheet }) {
  // Rows arrive one at a time on first paint so the sheet reads as being
  // WRITTEN rather than pasted — the same reason the old card staggered.
  // After that it updates in place: an answer landing must never restart the
  // stagger, or the sheet flickers on every turn.
  const [drawn, setDrawn] = useState(0);
  const firstRef = useRef(true);
  const total = (sheet && sheet.rows && sheet.rows.length) || 0;

  useEffect(() => {
    if (!total) { setDrawn(0); firstRef.current = true; return undefined; }
    if (!firstRef.current) { setDrawn(total); return undefined; }
    firstRef.current = false;
    setDrawn(0);
    const ts = Array.from({ length: total }, (_, i) => setTimeout(() => setDrawn(i + 1), 260 + i * 130));
    return () => ts.forEach(clearTimeout);
  }, [total]);

  if (!sheet || !Array.isArray(sheet.rows) || !sheet.rows.length) return null;
  const kind = String(sheet.kind || 'plain');
  const mark = KIND_MARK[kind] || KIND_MARK.plain;
  const filled = sheet.filled || 0;

  return (
    <div className={`psheet psheet--${kind}`} role="group" aria-label={`${sheet.sheetWord}: ${sheet.title}`}>
      <div className="psheet__head">
        <div className="psheet__kicker">
          <span className="psheet__mark" aria-hidden="true">{mark}</span>
          {sheet.sheetWord}
        </div>
        <div className="psheet__title">{sheet.title}</div>
        <div className="psheet__meter" aria-label={`${filled} of ${sheet.total} answered`}>
          {sheet.rows.map((r, i) => (
            <span key={i} className={`psheet__pip${r.a ? ' is-filled' : ''}${r.state === 'asking' ? ' is-asking' : ''}`} />
          ))}
        </div>
      </div>

      <ol className="psheet__rows">
        {sheet.rows.map((r, i) => (
          <li
            key={`${r.n}-${r.label}`}
            className={`psheet__row is-${r.state}${i < drawn ? ' is-drawn' : ''}`}
            aria-current={r.state === 'asking' ? 'step' : undefined}
          >
            <span className="psheet__n">{r.n}</span>
            <div className="psheet__body">
              <div className="psheet__label">{r.label}</div>
              {/* THEIR WORDS ARE THE ROW (Braeden 9/6): "we need to list the
                  things the user says ... not what she comes up with." Once a
                  row is answered, what they said IS the content and her
                  question drops to a caption under it. An unanswered row has
                  nothing of theirs yet, so it shows the question. Nothing is
                  summarised or reworded - it is their sentence, verbatim. */}
              {r.a ? (
                <>
                  <div className="psheet__a">{r.a}</div>
                  <div className="psheet__qsmall">{r.q}</div>
                </>
              ) : (
                <>
                  <div className="psheet__q">{r.q}</div>
                  <div className="psheet__blank" aria-hidden="true" />
                </>
              )}
            </div>
          </li>
        ))}
      </ol>

      {sheet.move ? (
        <div className="psheet__move">
          <span className="psheet__moveLabel">{sheet.moveLabel || 'First move'}</span>
          <span className="psheet__moveText">{sheet.move}</span>
        </div>
      ) : null}
    </div>
  );
}
